"use client";

import { useEffect, useMemo, useState } from "react";
import { NewCreditCardForm } from "../forms/new-credit-card-form";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { useCreditCardBuyStore, useTokenStore } from "@/lib/zustand";
import { useParaAccount } from "@/hooks/use-appkit-account";
import { Loader2 } from "lucide-react";
import { usePortfolioStore } from "../wallet/mobile-portfolio-toggle";
import Image from "next/image";
import { useWallet } from "@getpara/react-sdk";
import { WertRegionsModal } from "./wert-regions-modal";

type OnRampSelection = {
  chainId: number;
  symbol?: string;
};

const CardHelpTooltip = () => {
  const [showCardHelp, setShowCardHelp] = useState(false);
  const [showRegionsModal, setShowRegionsModal] = useState(false);

  return (
    <div className="flex flex-col items-center mt-2">
      <button
        type="button"
        onClick={() => setShowCardHelp(true)}
        className="text-xs text-gray-400 hover:text-gray-300 underline underline-offset-2 transition-colors"
      >
        Having issues buying with a card?
      </button>
      {showCardHelp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowCardHelp(false)}
          />
          <div className="relative z-10 bg-[#111] border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <button
              type="button"
              onClick={() => setShowCardHelp(false)}
              className="absolute top-4 right-4 p-1 rounded-lg hover:bg-white/10 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h3 className="text-white font-semibold text-base mb-3 pr-8">
              Trouble with card purchases?
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-3">
              Some banks automatically block crypto-related purchases as a security measure.
            </p>
            <p className="text-gray-400 text-sm leading-relaxed mb-3">
              <span className="text-white font-medium">What to do:</span> Call your bank or card provider and ask them to allow transactions from <span className="text-white">Wert</span> — the payment processor used by ForgeSwap.
            </p>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Alternatively, try paying with <span className="text-white">Apple Pay</span> or <span className="text-white">Google Pay</span> which often have fewer restrictions.
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setShowCardHelp(false)}
                className="w-full py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition-colors"
              >
                Got it
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCardHelp(false);
                  setShowRegionsModal(true);
                }}
                className="w-full py-2 rounded-xl border border-white/10 hover:bg-white/5 text-gray-400 hover:text-white text-sm font-medium transition-colors"
              >
                Check supported regions
              </button>
            </div>
          </div>
        </div>
      )}
      <WertRegionsModal
        isOpen={showRegionsModal}
        onClose={() => setShowRegionsModal(false)}
      />
    </div>
  );
};

export const PurchaseWithCreditCard = ({
  selectedOnRampAsset,
}: {
  selectedOnRampAsset?: OnRampSelection | null;
}) => {
  const { isConnected } = useParaAccount();
  const { data: paraWallet } = useWallet();
  const paraIdentity = paraWallet as
    | { email?: string | null; userId?: string | null; externalId?: string | null }
    | undefined;
  const isParaEmailWalletConnected = !!(
    paraIdentity?.email ||
    paraIdentity?.userId ||
    paraIdentity?.externalId
  );
  const isWalletConnected = isConnected || isParaEmailWalletConnected || !!paraWallet?.address;
  const { toToken, fromTokenAmountUSD, toTokenAmountUSD } = useTokenStore();
  const { isWertWidgetOpen, selectedOnRampChainId } = useCreditCardBuyStore();
  const [isOpen, setIsOpen] = useState(false);
  const { setIsOpen: setPortfolioOpen } = usePortfolioStore();
  const selectedChainId =
    selectedOnRampAsset?.chainId ?? selectedOnRampChainId ?? toToken?.chainId;
  const selectedSymbol = selectedOnRampAsset?.symbol ?? toToken?.symbol;

  // Pre-fill amount from swap state, fallback to $5
  const defaultAmount = useMemo(() => {
    if (Number(fromTokenAmountUSD) > 0) {
      return fromTokenAmountUSD;
    }
    if (Number(toTokenAmountUSD) > 0) {
      return toTokenAmountUSD;
    }
    return "5";
  }, [fromTokenAmountUSD, toTokenAmountUSD]);

  // Handler to close modal when Wert widget opens
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  // Close modal when widget opens
  useEffect(() => {
    if (isWertWidgetOpen && isOpen) {
      setIsOpen(false);
      setPortfolioOpen(false); // Also close portfolio sidebar just in case
    }
  }, [isWertWidgetOpen, isOpen, setPortfolioOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <div className="flex flex-col gap-2 w-full">
        <DialogTrigger asChild>
          <Button
            type="button"
            className="w-full rounded-[10px] bg-[linear-gradient(135deg,#f9cd4f_0%,#d4a012_100%)] text-[#111] font-bold shadow-[0_0_20px_rgba(249,205,79,0.2)] hover:shadow-[0_0_30px_rgba(249,205,79,0.35)]"
            disabled={!isWalletConnected || isWertWidgetOpen}
            onPointerDown={(event) => {
              if (event.pointerType !== "mouse" && !isWertWidgetOpen && isWalletConnected) {
                setIsOpen(true);
              }
            }}
          >
            {isWertWidgetOpen ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <p>BUY {selectedSymbol || ""} WITH DEBIT/CREDIT CARD</p>
            )}
          </Button>
        </DialogTrigger>

        <CardHelpTooltip />

        {/* Apple Pay / Google Pay buttons now open the modal for amount entry */}
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex items-center justify-center gap-2 w-full min-w-0 overflow-hidden rounded-[8px] border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)] px-3 py-2 text-xs text-[#aaa] hover:bg-[rgba(255,255,255,0.05)]"
            disabled={!isWalletConnected || isWertWidgetOpen}
            onClick={() => setIsOpen(true)}
            onPointerDown={(event) => {
              if (event.pointerType !== "mouse" && !isWertWidgetOpen && isWalletConnected) {
                event.preventDefault();
                setIsOpen(true);
              }
            }}
          >
            <Image
              src="/applepay.png"
              alt="Apple Pay"
              width={54}
              height={18}
              className="h-[18px] w-auto shrink-0"
            />
            <span className="min-w-0 truncate text-sm">Buy with Apple Pay</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            className="flex items-center justify-center gap-2 w-full min-w-0 overflow-hidden rounded-[8px] border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)] px-3 py-2 text-xs text-[#aaa] hover:bg-[rgba(255,255,255,0.05)]"
            disabled={!isWalletConnected || isWertWidgetOpen}
            onClick={() => setIsOpen(true)}
            onPointerDown={(event) => {
              if (event.pointerType !== "mouse" && !isWertWidgetOpen && isWalletConnected) {
                event.preventDefault();
                setIsOpen(true);
              }
            }}
          >
            <Image
              src="/googlepay.png"
              alt="Google Pay"
              width={54}
              height={18}
              className="h-[18px] w-auto shrink-0"
            />
            <span className="min-w-0 truncate text-sm">Buy with Google Pay</span>
          </Button>
        </div>
      </div>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-primary">
            Buy {selectedSymbol || ""} with Debit/Credit Card
          </DialogTitle>
          <DialogClose />
        </DialogHeader>
        <NewCreditCardForm defaultAmount={defaultAmount} />
      </DialogContent>
    </Dialog>
  );
};
